import { TimeZone } from 'sharebase/services/timeZones';
import { TrackingService } from 'sharebase/services/trackingService';

require('./timeZonePicker.styles');
const template = require('./timeZonePicker.template');

/**
 * @ngdoc directive
 * @name sharebase.timezones.sbTimeZonePicker
 * @module sharebase.timezones
 * @restrict E
 * @description
 * 
 * `sbTimeZonePicker` is a simple form control that uses the `ngModel` directive
 * to represent it's value and the validity of the value. The control displays an
 * autocomplete box for users to choose a time zone. It is agnostic of any backing
 * storage service, so it can be used for multiple purposes. You can use `ngMessages`
 * between the opening and closing tag to display form validation messages. Note that
 * the `ngMessages` element must be a `div`.
 * 
 * @element input
 * @param {TimeZone=} ngModel A {@link TimeZone} object for the control's value.
 * @param {string@} forSection An optional parameter that notes what the picker is being used for.
 * This value will be used as the eventTarget for {@link TrackingService#trackClick}.
 * @param {'left'|'right'@} helpIconLocation Optional input to specify how the help icon is displayed.
 * Defaults to 'right'.
 *
 * @example <caption>Display error messages via ng-messages</caption>
 * ```
 * <sb-time-zone-picker name="timeZone" ng-model="vm.timeZone" required for-section="Admin">
 *     <div ng-messages="vm.form.timeZone.$error">
 *         <div ng-message="required">Time zone is required.</div>
 *     </div>
 * </sb-time-zone-picker>
 * ```
 * 
 * @example <caption>Use picker without displaying error messages</caption>
 * ```
 * <sb-time-zone-picker name="timeZone" ng-model="vm.timeZone" ng-required="vm.hasSavedTimeZone" for-section="My Account">
 * </sb-time-zone-picker>
 * ```
 */
export function TimeZonePickerDirective() {
    return {
        restrict: 'E',
        template,
        require: { ngModelCtrl: 'ngModel' },
        bindToController: true,
        scope: {
            ngModel: '=',
            forSection: '=',
            onChange: '&'
        },
        controller: TimeZonePickerController,
        controllerAs: 'vm',
        transclude: {
            errorMessages: '?div'
        }
    }
}

type HelpIconLocation = 'left' | 'right';

export class TimeZonePickerController {
    public static readonly $inject = [
        '$transclude',
        '$mdMedia',
        'sbTimeZones',
        'sbTrackingService'
    ];

    private get timeZone(): TimeZone {
        return this.ngModelCtrl.$modelValue;
    }

    private set timeZone(value: TimeZone) {
        this.ngModelCtrl.$setViewValue(value);
        this.searchText = '';

        if(this.onChange) {
            this.onChange();
        }

        if(value) {
            this.trackTimeZoneChange();
        }
    }

    public searchText:string;

    //Passed into directive's isolated scope
    public forSection:string;
    public onChange:Function;
    public ngModelCtrl:ng.INgModelController;

    //Used in template
    private hasNgMessages:boolean = false;

    constructor(
        private readonly $transclude,
        private readonly $mdMedia, //Used in template
        private readonly timeZones: TimeZone[],
        private readonly tracking: TrackingService
    ) {
    }

    public $onInit(): void {
        this.hasNgMessages = this.$transclude.isSlotFilled('errorMessages');
    }

    public searchTimeZones(searchText: string): TimeZone[] {
        if (!searchText) {
            return this.timeZones;
        } else {
            return this.timeZones.filter(tz => tz.label.toLocaleLowerCase().indexOf(searchText.toLocaleLowerCase()) >= 0);
        }
    }

    private trackTimeZoneChange(): void {
        let evTarget = this.forSection || 'TimeZonePicker';
        let text = 'Configure time zone';
        this.tracking.trackClick(text, evTarget);
    }

}


// WEBPACK FOOTER //
// ./src/sharebase/components/timeZonePicker/timeZonePicker.ts